import { Helmet } from 'react-helmet-async';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useAppContext } from '@contexts/AppContext';
import { getOutletAbsoluteUrl, getShafaAbsoluteUrl } from '@utils/common';
import { getPathWithoutLang } from '@utils/i18n';
import { Link } from '@ui/Link';

import s from './Breadcrumbs.sss';

export interface Breadcrumb {
  name: string,
  url: string,
}

interface Props {
  className?: string,
  direction?: 'row' | 'column',
  breadcrumbs: Breadcrumb[],
}

export const Breadcrumbs: React.FC<Props> = ({
  className,
  breadcrumbs,
  direction,
}) => {
  const history = useHistory();
  const { isOutlet } = useAppContext();

  const breadcrumbsWithRoot = [
    {
      name: isOutlet ? 'Outlet' : 'Shafa.ua',
      url: history.createHref({ pathname: '/' }),
    },
    ...breadcrumbs,
  ];

  return (
    <>
      <Helmet>
        {/*
          Breadcrumb structured data according to:
          https://developers.google.com/search/docs/appearance/structured-data/breadcrumb#json-ld
        */}
        <script type="application/ld+json">
          {
            JSON.stringify(
              {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbsWithRoot.map(
                  (breadcrumb, i) => {
                    // Build absolute url
                    const url = isOutlet
                      ? getOutletAbsoluteUrl(breadcrumb.url)
                      : getShafaAbsoluteUrl(breadcrumb.url);

                    return {
                      '@type': 'ListItem',
                      position: i + 1,
                      name: breadcrumb.name,
                      item: url,
                    };
                  },
                ),
              },
            )
          }
        </script>
      </Helmet>

      <ul
        className={classNames(s.list, className, {
          [s.listDirectionColumn]: direction === 'column',
        })}
      >
        {breadcrumbsWithRoot.map((breadcrumb) => {
          return (
            <li
              key={breadcrumb.name}
              className={s.item}
            >
              <Link
                className={s.link}
                theme='black'
                to={getPathWithoutLang(breadcrumb.url)}
              >
                {breadcrumb.name}
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
};
